/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {Box, Chip, FormLabel, MenuItem, Select} from '@mui/material';
import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridColDef,
  type GridFilterItem,
} from '@mui/x-data-grid-premium';
import {merge} from 'lodash';
import React from 'react';
import {toDate} from './dates';
import {getColorFromPercentage} from './getColorFromPercentage';
import {looksLikeDateField, looksLikeNumberField, makeColumnNamePrettier} from './super-table-utils';
import SuperChart from '../components/SuperTable/SuperChart';

export type FieldConfig = {
  label?: string;
  type?: 'date' | 'percentage' | 'string' | 'number' | 'boolean';
  hide?: boolean;
  groupable?: boolean;
  aggregable?: boolean;
};

export const COMMON_FIELDS: Record<string, FieldConfig> = {
  agentActionpd: {label: 'Agent Action', type: 'string', hide: true},
  agentSentimentpd: {label: 'Agent Sentiment', type: 'number'},
  aniDisplayable: {label: 'ANI Displayable', type: 'string', hide: true},
  aniNormalized: {label: 'Caller Number', type: 'string', groupable: true},
  averageScore: {label: 'Trusst Score', type: 'percentage', hide: false},
  softSkillsTotalScore: {label: 'Soft Skills Score', type: 'percentage'},
  processTotalScore: {label: 'Process Score', type: 'percentage'},
  productTotalScore: {label: 'Product Score', type: 'percentage'},
  complianceTotalScore: {label: 'Compliance', type: 'percentage'},
  categorypd: {label: 'Primary Category', groupable: true},
  channel: {label: 'Channel', groupable: true, hide: true},
  churnpd: {label: 'Churn Risk', type: 'number', hide: false},
  completedAt: {label: 'Completed At', type: 'date', hide: true},
  connectInstanceId: {label: 'Connect Instance ID', hide: true},
  contactId: {label: 'Contact ID', type: 'string', hide: false},
  contactImportId: {hide: true},
  conversationId: {label: 'Conversation ID', hide: true},
  createdAt: {label: 'Processed At', type: 'date', hide: true},
  criteriaId: {label: 'Criteria ID', type: 'string', hide: true},
  criteriaName: {label: 'Criteria', type: 'string', hide: false, groupable: true},
  customerEffortpd: {label: 'Customer Effort', type: 'number'},
  customerProblempd: {label: 'Customer Problem', type: 'string'},
  customerSentimentpd: {label: 'Customer Sentiment', type: 'number'},
  dnisDisplayable: {label: 'DNIS Displayable', type: 'string', hide: true},
  dnisNormalized: {hide: true},
  durationMs: {label: 'Duration', type: 'number'},
  endTime: {label: 'End Time', type: 'date'},
  filePath: {label: 'File Path', hide: true},
  fileSize: {label: 'File Size', type: 'number', hide: true},
  id: {hide: true},
  initialDirection: {label: 'Initial Direction', groupable: true},
  intentpd: {label: 'Intent', groupable: true},
  mediaType: {label: 'Media Type', groupable: true, hide: true},
  metadataUpdatedAt: {label: 'Metadata Updated At', type: 'date', hide: true},
  nPSpd: {label: 'NPS', type: 'number'},
  organizationId: {label: 'Organization ID', hide: true},
  productpd: {label: 'Product', type: 'string', groupable: true},
  promptRevisionId: {label: 'Prompt Revision ID', type: 'string', hide: true},
  promptRevisionName: {label: 'Prompt Revision Name', type: 'string', hide: true},
  provider: {label: 'Provider', groupable: true, hide: true},
  queueIds: {label: 'Queue IDs', groupable: true},
  recordingId: {label: 'Recording ID', hide: true},
  repeatCallpd: {label: 'Repeat Call', type: 'boolean'},
  resolutionpd: {label: 'Resolution', type: 'boolean'},
  screenInformation: {label: 'Screen Information', type: 'string', hide: true},
  source: {hide: true},
  startTime: {label: 'Initiation Time', type: 'date'},
  startedAt: {label: 'Started At', type: 'date', hide: true},
  status: {label: 'Status', type: 'string', groupable: true},
  'sub-Categorypd': {label: 'Secondary Category', groupable: true},
  'sub-Sub-Categorypd': {label: 'Tertiary Category', groupable: true},
  summarypd: {label: 'Summary', type: 'string'},
  totalcompliancescore: {label: 'Compliance Score', type: 'number'},
  totalnumrows: {hide: true},
  totalprocessscore: {label: 'Process Score', type: 'number'},
  totalproductscore: {label: 'Product Score', type: 'number'},
  totalsoftskillscore: {label: 'Soft Skills Score', type: 'number'},
  updatedAt: {label: 'Updated At', type: 'date', hide: true},
  userIds: {label: 'User IDs', groupable: true},
  wrapupCodes: {label: 'Wrap-up Codes', groupable: true},
  year: {label: 'Year', type: 'number', groupable: true, hide: true},
};

export function toColumns<T extends Record<string, any>>(
  data: T,
  customFields: Record<string, FieldConfig> = {},
): GridColDef[] {
  const knownFields = merge({}, COMMON_FIELDS, customFields);
  const uniqueFields = new Set<string>(['id']);
  Object.values(data).forEach((entries) => {
    entries.forEach(({key}: {key: string}) => uniqueFields.add(key));
  });

  // Define the exact order for specific fields
  const orderedFields = [
    'status',
    'averageScore',
    'softSkillsTotalScore',
    'processTotalScore',
    'productTotalScore',
    'complianceTotalScore',
  ];

  const defaultWidth = 150;
  const columns = Array.from(uniqueFields).reduce<GridColDef[]>((cols, field) => {
    const config = knownFields[field] || {};
    if (config.hide) return cols;

    const colDefDefaults: GridColDef = {
      field,
      headerName: config.label || makeColumnNamePrettier(field),
      width: defaultWidth,
      valueOptions: [],
      filterable: false,
      filterOperators: defaultFilterOperators,
      sortable: true,
      disableColumnMenu: false,
      disableReorder: false,
      disableExport: false,
      aggregable: !!config.aggregable,
      groupable: !!config.groupable,
    };

    if (field === 'durationMs') {
      cols.push(colDefDuration(colDefDefaults));
    } else if (field === 'initialDirection') {
      cols.push(colDefInitialDirection(colDefDefaults));
    } else if (field === 'status') {
      cols.push(colDefStatus(colDefDefaults));
    } else if (field === 'fileSize') {
      // Skip fileSize column (hidden)
      return cols;
    } else if (field === 'criteriaId') {
      cols.push(colDefCriteriaId(colDefDefaults));
    } else {
      switch (config.type) {
        case 'boolean':
          cols.push(colDefBoolean(colDefDefaults));
          break;
        case 'percentage':
          cols.push(colDefPercentage(colDefDefaults));
          break;
        case 'number':
          cols.push(colDefNumber(colDefDefaults));
          break;
        case 'date':
          cols.push(colDefDate(colDefDefaults));
          break;
        case 'string':
          cols.push(colDefString(colDefDefaults));
          break;
        default:
          if (looksLikeDateField(field)) {
            cols.push(colDefDate(colDefDefaults));
          } else if (looksLikeNumberField(field)) {
            cols.push(colDefNumber(colDefDefaults));
          } else {
            cols.push(colDefString(colDefDefaults));
          }
          break;
      }
    }
    return cols;
  }, []);

  return columns.sort((a, b) => {
    const aIndex = orderedFields.indexOf(a.field);
    const bIndex = orderedFields.indexOf(b.field);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }

    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return 0;
  });
}

interface ToRowsOptions {
  idField?: string;
}

export function toRows<T extends Record<string, any>>(data: T, {idField}: ToRowsOptions = {}) {
  return Object.entries(data).map(([outerKey, entries]) => {
    const row: Record<string, any> = {};
    for (const {key, value, isSuper} of entries) {
      let processedValue = value;

      // Handle `isSuper` flag to parse JSON
      if (isSuper) {
        try {
          processedValue = value ? JSON.parse(value) : value;
        } catch (err) {
          console.warn(`Error parsing JSON for key '${key}' with value:`, value);
        }
      }

      // Explicitly handle arrays (convert to a comma-separated string)
      if (Array.isArray(processedValue)) {
        processedValue = processedValue.join(', ');
      }
      row[key] = processedValue;
      if (idField && key === idField) {
        row.id = processedValue;
      }
    }

    // Set default `id` in case it's not set via `idField`
    if (row.id === null || row.id === undefined) {
      row.id = outerKey;
    }
    return row;
  });
}

const [
  equalsNumericOperator,
  _notEqualsNumericOperator,
  greaterThanNumericOperator,
  greaterThanOrEqualsNumericOperator,
  lessThanNumericOperator,
  lessThanOrEqualsNumericOperator,
  _isEmptyNumericOperator,
  _isNotEmptyNumericOperator,
  _isAnyOfNumberOperator,
] = getGridNumericOperators();

const [
  _isDateOperator,
  _notDateOperator,
  _afterDateOperator,
  _onOrAfterDateOperator,
  _beforeDateOperator,
  _onOrBeforeDateOperator,
  _isDateEmptyOperator,
  _isDateNotEmptyOperator,
] = getGridDateOperators();

const [
  containsStringOperator,
  _doesNotContainStringOperator,
  equalsStringOperator,
  _doesNotEqualStringOperator,
  startsWithStringOperator,
  endsWithStringOperator,
  _isStringEmptyOperator,
  _isStringNotEmptyOperator,
  _isAnyOfStringOperator,
] = getGridStringOperators();

const [isOperator, _notOperator, _isAnyOfSelectOperator] = getGridSingleSelectOperators();

const defaultFilterOperators = [equalsStringOperator];

const numberFilterOperators = [
  equalsNumericOperator,
  greaterThanNumericOperator,
  greaterThanOrEqualsNumericOperator,
  lessThanNumericOperator,
  lessThanOrEqualsNumericOperator,
];

const stringFilterOperators = [
  containsStringOperator,
  equalsStringOperator,
  startsWithStringOperator,
  endsWithStringOperator,
];

interface NumericValue {
  number?: number | string;
}

const numericSortComparator = (v1: NumericValue | undefined, v2: NumericValue | undefined) => {
  const num1 = v1?.number !== undefined ? Number(v1.number) : Number.MIN_SAFE_INTEGER;
  const num2 = v2?.number !== undefined ? Number(v2.number) : Number.MIN_SAFE_INTEGER;
  if (isNaN(num1) && isNaN(num2)) return 0;
  if (isNaN(num1)) return -1;
  if (isNaN(num2)) return 1;
  return num1 - num2;
};
const colDefBoolean = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  filterable: true,
  filterOperators: [
    {
      ...isOperator,
      InputComponent: ({item, applyValue}) => (
        <>
          <FormLabel component={'legend'} sx={{fontSize: 12, position: 'absolute', top: 0, left: 0}}>
            Value
          </FormLabel>
          <Select
            fullWidth
            onChange={(event) => {
              applyValue({...item, value: event.target.value || null});
            }}
            sx={{mt: 1}}
            value={item.value === null ? '' : item.value}
          >
            {[
              {value: undefined, label: 'Any'},
              {value: 'true', label: 'Yes'},
              {value: 'false', label: 'No'},
            ].map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </>
      ),
      label: 'Is',
      value: 'equals',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.value) return null;
        return (value) => value === filterItem.value;
      },
    },
  ],
  groupable: true,
  renderCell: (params) => {
    const {value} = params;
    if (value === undefined) return null;
    const boolVal = value === 'true' || value === true;
    return (
      <Chip label={boolVal ? 'Yes' : 'No'} color={boolVal ? 'success' : 'error'} variant={'outlined'} size={'small'} />
    );
  },
  type: 'singleSelect',
  valueOptions: [
    {value: undefined, label: 'Any'},
    {value: 'true', label: 'Yes'},
    {value: 'false', label: 'No'},
  ],
  width: 130,
});

const colDefStatus = (colDefDefaults: GridColDef): GridColDef => {
  const statusOptions = [
    {value: undefined, label: 'Any'},
    {value: 'COMPLETED', label: 'Completed'},
    {value: 'INCOMPLETE', label: 'Incomplete'},
  ];
  return {
    ...colDefDefaults,
    filterable: true,
    filterOperators: [
      {
        ...isOperator,
        InputComponent: ({item, applyValue}) => (
          <>
            <FormLabel component={'legend'} sx={{fontSize: 12, position: 'absolute', top: 0, left: 0}}>
              Value
            </FormLabel>
            <Select
              fullWidth
              onChange={(event) => {
                applyValue({...item, value: event.target.value || null});
              }}
              sx={{mt: 1}}
              value={item.value === null ? '' : item.value}
            >
              {statusOptions.map((option, index) => (
                <MenuItem key={option.value || index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </>
        ),
        label: 'Is',
        value: 'equals',
        getApplyFilterFn: (filterItem: GridFilterItem) => {
          if (!filterItem.value) return null;
          return (value) => value === filterItem.value;
        },
      },
    ],
    groupable: true,
    renderCell: (params) => {
      const {value} = params;
      if (value === undefined) return null;
      const isCompleted = value === 'COMPLETED';
      return (
        <Chip
          label={isCompleted ? 'Completed' : 'Incomplete'}
          color={isCompleted ? 'success' : 'error'}
          variant={'outlined'}
          size={'small'}
        />
      );
    },
    type: 'singleSelect',
    valueOptions: statusOptions,
    width: 130,
  };
};

const colDefPercentage = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  valueGetter: (value, {startTime, id}) => {
    return {
      number: Number(value),
      time: new Date(startTime).getTime(),
      id: id,
    };
  },
  sortComparator: numericSortComparator,
  renderCell: (params) => {
    const {value} = params;
    if (Array.isArray(value)) {
      return <SuperChart data={value} type={'percentage'} title={params.colDef.headerName} />;
    }
    if (value === undefined || isNaN(Number(value.number))) return null;
    const percentage = Number(value.number).toFixed(0);
    return <Box sx={{color: getColorFromPercentage(Number(value.number))}}>{percentage}%</Box>;
  },
  type: 'number',
  align: 'right',
  filterable: true,
  filterOperators: numberFilterOperators,
  aggregable: true,
  availableAggregationFunctions: ['avg', 'visualise'],
  width: 130,
});

const colDefNumber = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  type: 'number',
  valueGetter: (value, {startTime, contactId}) => ({
    number: Number(value),
    time: new Date(startTime).getTime(),
    id: contactId,
  }),
  sortComparator: numericSortComparator,
  renderCell: (params) => {
    const {value} = params;
    if (Array.isArray(value)) {
      return <SuperChart data={value} type={'number'} title={params.colDef.headerName} />;
    }
    const {number} = value;
    if (number === undefined || isNaN(Number(number))) return null;
    const num = Number(number);
    return Number.isInteger(num) ? num.toString() : num.toFixed(2);
  },
  align: 'right',
  filterable: true,
  filterOperators: numberFilterOperators,
  aggregable: true,
  availableAggregationFunctions: ['sum', 'avg', 'min', 'max', 'visualise'],
  width: 130,
});

const colDefDate = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  valueGetter: (value) => toDate(value),
  width: 180,
});

const colDefString = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  filterable: true,
  filterOperators: stringFilterOperators,
});

// Converts duration from milliseconds to hh:mm:ss.
const colDefDuration = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  type: 'number',
  sortComparator: (v1, v2) => Number(v1) - Number(v2),
  valueGetter: (value) => (value ? Number(value) : null),
  renderCell: (params) => {
    const {value} = params;
    if (value === undefined || isNaN(Number(value))) return null;
    const ms = Number(value);
    const hours = Math.floor(ms / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  },
  align: 'right',
  filterable: true,
  filterOperators: [
    ...numberFilterOperators.map((operator) => ({
      ...operator,
      InputComponent: ({item, applyValue}: {item: any; applyValue: (value: any) => void}) => {
        const existingMs = item.value ? Number(item.value) : 0;
        const initialMins = Math.floor(existingMs / 60000).toString();
        const initialSecs = Math.floor((existingMs % 60000) / 1000).toString();

        const [mins, setMins] = React.useState<string>(initialMins);
        const [secs, setSecs] = React.useState<string>(initialSecs);

        const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newMins = e.target.value;
          setMins(newMins);
          const totalMs = parseInt(newMins || '0') * 60000 + parseInt(secs || '0') * 1000;
          applyValue({...item, value: totalMs});
        };

        const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newSecs = e.target.value;
          setSecs(newSecs);
          const totalMs = parseInt(mins || '0') * 60000 + parseInt(newSecs || '0') * 1000;
          applyValue({...item, value: totalMs});
        };

        return (
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 1}}>
            <Box sx={{display: 'flex', gap: 1}}>
              <Box sx={{display: 'flex', flexDirection: 'column', flex: 1}}>
                <FormLabel component={'legend'} sx={{fontSize: 12}}>
                  Minutes
                </FormLabel>
                <input
                  type={'number'}
                  min={'0'}
                  value={mins}
                  onChange={handleMinutesChange}
                  style={{
                    padding: '8px',
                    width: '100%',
                    borderRadius: '4px',
                  }}
                />
              </Box>
              <Box sx={{display: 'flex', flexDirection: 'column', flex: 1}}>
                <FormLabel component={'legend'} sx={{fontSize: 12}}>
                  Seconds
                </FormLabel>
                <input
                  type={'number'}
                  min={'0'}
                  max={'59'}
                  value={secs}
                  onChange={handleSecondsChange}
                  style={{
                    padding: '8px',
                    width: '100%',
                    borderRadius: '4px',
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      },
    })),
  ],
});

const colDefInitialDirection = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  renderCell: (params) => {
    const {value} = params;
    if (value === undefined) return null;
    const valStr = String(value).toLowerCase();
    if (valStr === 'inbound') {
      return (
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <ArrowDownwardIcon sx={{color: 'green', mr: 0.5}} />
          Inbound
        </Box>
      );
    }
    if (valStr === 'outbound') {
      return (
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <ArrowUpwardIcon sx={{color: 'orange', mr: 0.5}} />
          Outbound
        </Box>
      );
    }
    return value;
  },
  filterable: true,
  filterOperators: defaultFilterOperators,
});

// temporary fix for prompt revision id until we refactor the reference to prompt end to end in TrusstGPT
const colDefCriteriaId = (colDefDefaults: GridColDef): GridColDef => ({
  ...colDefDefaults,
  renderCell: (params) => {
    const {value} = params;
    if (value === undefined) return null;
    return `${value.replace('prompt-', 'criteria-')}`;
  },
});
